import { useState } from "react";
import { Mail, MapPin, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/data/site";

const input =
  "w-full rounded-xl border border-sand bg-ivory px-4 py-3.5 text-sm outline-none transition-colors placeholder:text-ink/40 focus:border-ink";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory px-5 py-16 md:px-10 md:py-24" data-testid="contact-page">
      <div className="mx-auto grid max-w-[1100px] gap-16 md:grid-cols-2">
        <Reveal>
          <p className="eyebrow text-gold">Get in Touch</p>
          <h1 className="mt-3 font-serif text-5xl font-light tracking-tight text-ink md:text-7xl">Contact Us</h1>
          <p className="mt-6 max-w-md text-ink/70">
            Questions about your order, styling, or care? Our team would love to help. We usually
            reply within one business day.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/40 text-gold">
                <Mail size={17} strokeWidth={1.5} />
              </span>
              <span className="text-sm text-ink/70">{BRAND.email}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/40 text-gold">
                <Instagram size={17} strokeWidth={1.5} />
              </span>
              <span className="text-sm text-ink/70">{BRAND.handle}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/40 text-gold">
                <MapPin size={17} strokeWidth={1.5} />
              </span>
              <span className="text-sm text-ink/70">Studio Noora · Worldwide Shipping</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-4 rounded-2xl bg-cream p-8" data-testid="contact-form">
            <input required placeholder="Your name" value={form.name} onChange={upd("name")} className={input} data-testid="contact-name" />
            <input required type="email" placeholder="Email address" value={form.email} onChange={upd("email")} className={input} data-testid="contact-email" />
            <textarea required rows={5} placeholder="How can we help?" value={form.message} onChange={upd("message")} className={`${input} resize-none`} data-testid="contact-message" />
            <button type="submit" disabled={busy} className="w-full rounded-full bg-ink py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold disabled:opacity-60" data-testid="contact-submit">
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
